// UsersContext — single source of truth for the user list.
//
// Demo mode:  Maintains DEMO_USERS as local React state. Mutations (add, edit,
//             remove) flow through setUsers so User Management and the
//             assign-to dropdowns always see the same live list.
//
// Live mode:  Fetches from GET /api/users via TanStack Query, same as
//             UserManagement.tsx was already doing inline. Read-only from the
//             context's perspective — mutations go through the API directly
//             and invalidate the query on success.
//
// Charlotte / Devin wiring notes:
//   - In live mode the context calls getAllUsers() which hits GET /api/users.
//     The response shape UserListItemApi is mapped to AppUser here; you do not
//     need to change the API contract.
//   - If you need the "current user" as a separate concept, that remains in
//     AppContext (via useUserProfile). UsersContext is only the *full* list.
//   - getUserById(id) is the safe resolver — use it instead of inline PERSONAS
//     lookups anywhere you need a name or role from a user ID.

import { useQuery } from "@tanstack/react-query";
import React, {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { getAllUsers } from "@/api/services/userService";
import { useApp } from "@/contexts/AppContext";
import type { AppUser } from "@/types/user";

// ── Demo seed data ────────────────────────────────────────────────────────────
// Keep this list in sync with UserManagement.tsx DEMO_USERS.
// When UserManagement.tsx adds or removes a user in demo mode it calls
// setUsers() from this context — the two are the same in-memory state.

export const DEMO_USERS: AppUser[] = [
  {
    id: "1",
    name: "Catherine Hughes",
    email: "catherine.hughes@buildright.co.uk",
    forecasterRole: "CEO",
    systemRole: "User",
  },
  {
    id: "2",
    name: "David Chen",
    email: "david.chen@buildright.co.uk",
    forecasterRole: "System Administrator",
    systemRole: "Administrator",
  },
  {
    id: "3",
    name: "Edna Foster",
    email: "edna.foster@buildright.co.uk",
    forecasterRole: "User",
    systemRole: "User",
  },
  {
    id: "4",
    name: "Linda Kowalski",
    email: "linda.kowalski@buildright.co.uk",
    forecasterRole: "CFO",
    systemRole: "User",
  },
  {
    id: "5",
    name: "Marcus Patel",
    email: "marcus.patel@buildright.co.uk",
    forecasterRole: "Regional Manager",
    systemRole: "User",
  },
  {
    id: "6",
    name: "Sarah Mitchell",
    email: "sarah.mitchell@buildright.co.uk",
    forecasterRole: "Finance Manager",
    systemRole: "User",
  },
  {
    id: "7",
    name: "Emma Thompson",
    email: "emma.thompson@buildright.co.uk",
    forecasterRole: "User",
    systemRole: "User",
  },
  {
    id: "8",
    name: "Jennifer O'Sullivan",
    email: "jennifer.osullivan@boardmember.ie",
    forecasterRole: "External User",
    systemRole: "External User",
  },
  {
    id: "9",
    name: "John Smith",
    email: "john.smith@buildright.co.uk",
    forecasterRole: "User",
    systemRole: "User",
  },
];

// ── Context shape ─────────────────────────────────────────────────────────────

interface UsersContextType {
  /** Full list of users visible to the application. */
  users: AppUser[];

  /** True while the live-mode API fetch is in flight. Always false in demo mode. */
  isLoading: boolean;

  /** Resolve a user by their ID. Returns undefined if not found. */
  getUserById: (id: string) => AppUser | undefined;

  /** Returns "Name (Role)" — the canonical display label for any user in lists and dropdowns. */
  getDisplayLabel: (id: string) => string;

  /**
   * Replace the full user list (demo mode only — no-op in live mode).
   * Called by UserManagement when a user is added, edited, or removed.
   */
  setUsers: (users: AppUser[]) => void;
}

const UsersContext = createContext<UsersContextType | undefined>(undefined);

// ── Provider ──────────────────────────────────────────────────────────────────

export const UsersProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { mode } = useApp();
  const isLive = mode === "live";

  // Demo state — mutable so User Management can add/edit/remove users
  const [demoUsers, setDemoUsers] = useState<AppUser[]>(DEMO_USERS);

  // Live mode — TanStack Query, same endpoint UserManagement.tsx was already calling
  const { data: apiUsersData, isLoading } = useQuery({
    queryKey: ["users"],
    queryFn: getAllUsers,
    enabled: isLive,
    staleTime: 0,
    refetchOnWindowFocus: true,
  });

  const users: AppUser[] = useMemo(() => {
    if (isLive && apiUsersData) {
      return apiUsersData.users.map((u) => ({
        id: u.userId,
        name: u.name,
        email: u.email,
        forecasterRole: u.roleName,
        systemRole: u.platformRole as AppUser["systemRole"],
      }));
    }
    return demoUsers;
  }, [isLive, apiUsersData, demoUsers]);

  const getUserById = useCallback(
    (id: string) => users.find((u) => u.id === id),
    [users],
  );

  const getDisplayLabel = useCallback(
    (id: string) => {
      const u = users.find((u) => u.id === id);
      return u ? `${u.name} (${u.forecasterRole})` : id;
    },
    [users],
  );

  const setUsers = useCallback(
    (updated: AppUser[]) => {
      if (!isLive) setDemoUsers(updated);
    },
    [isLive],
  );

  return (
    <UsersContext.Provider
      value={{ users, isLoading, getUserById, getDisplayLabel, setUsers }}
    >
      {children}
    </UsersContext.Provider>
  );
};

// ── Hook ──────────────────────────────────────────────────────────────────────

export const useUsers = (): UsersContextType => {
  const ctx = useContext(UsersContext);
  if (!ctx) throw new Error("useUsers must be used within a UsersProvider");
  return ctx;
};
